// ── All Participation Scheduled Payments — global list ──
// What we owe participants, across every loan with a participation: one row
// per participant scheduled payment, with its status (Paid / Not Due / Due,
// worked out by getParticipantScheduledPmts the same way as borrower
// scheduled payments) and how much has been paid against it. The same records
// as a loan's Participant Pmts tab; view, edit and delete open the same forms.
import { useState, useEffect } from "react";
import { localFilter, exportCSV, fmt, fmtDate } from "../../../shared/format";
import { Spinner, ErrorMsg, SectionHeader, SearchBar, Btn, Card, STh, Th, Td, TdNum, Badge, Icon, Pagination } from "../../../shared/ui";
import { useSortable, usePagination } from "../../../shared/hooks";
import { useApp } from "../../../shared/appContext";

export const AllParticipantSpmtsPage = () => {
  const { api, setModal, role, setConfirm, delRec } = useApp();
  const [rows, setRows] = useState([]); const [loading, setLoading] = useState(true); const [err, setErr] = useState(null);
  const [q, setQ] = useState("");
  const load = () => { setLoading(true); api.get("getParticipantScheduledPmts")
    .then(d => { setRows(Array.isArray(d) ? d : []); setLoading(false); })
    .catch(e => { setErr(e.message); setLoading(false); }); };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const filtered = localFilter(rows, q, ["loannumber", "companyname", "scheduleddate", "status", "feetype", "othertype"]);
  const { sorted, sortKey, sortDir, toggle } = useSortable(filtered, "scheduleddate", "desc");
  const { paginated: displayed, page, setPage, totalPages, total, reset } = usePagination(sorted, 100);
  const H = (label, k, numeric) => <STh label={label} sortKey={k} currentKey={sortKey} currentDir={sortDir} onSort={toggle} numeric={numeric} />;
  const view = p => setModal({ type: "pspmt-view", rec: { ...p, id: p.id, loanid: p.loanid } });

  if (loading) return <Spinner />; if (err) return <ErrorMsg msg={err} onRetry={load} />;
  return <>
    <SectionHeader actions={[
      <SearchBar key="s" value={q} onChange={v => { setQ(v); reset(); }} placeholder="Search participation scheduled payments..." />,
      <Btn key="ex" onClick={() => exportCSV(sorted, ["status", "loannumber", "companyname", "scheduleddate", "scheduledprincipal", "scheduledinterest",
        "feetype", "scheduledfee", "othertype", "scheduledother", "totalscheduledpayment", "totalreceivedpmt"], "participation_scheduled_pmts.csv")}>Export CSV</Btn>
    ]} />
    <Card style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", whiteSpace: "nowrap" }}>
        <thead><tr>
          {H("Status", "status")}{H("Loan #", "loannumber")}{H("Account", "companyname")}{H("Sched. Date", "scheduleddate")}
          {H("Principal", "scheduledprincipal", true)}{H("Interest", "scheduledinterest", true)}{H("Fee Type", "feetype")}
          {H("Fee", "scheduledfee", true)}{H("Other Type", "othertype")}{H("Other", "scheduledother", true)}
          {H("Total Sched", "totalscheduledpayment", true)}{H("Paid", "totalreceivedpmt", true)}<Th></Th>
        </tr></thead>
        <tbody>{displayed.map(p => (
          <tr key={p.id} style={{ cursor: "pointer" }} onClick={() => view(p)}
              onMouseEnter={e => e.currentTarget.style.background = "var(--color-background-secondary)"} onMouseLeave={e => e.currentTarget.style.background = ""}>
            <Td><Badge s={p.status || "—"} /></Td>
            <Td><span style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{p.loannumber}</span></Td>
            <Td style={{ fontWeight: 500 }}>{p.companyname}</Td>
            <Td>{fmtDate(p.scheduleddate)}</Td>
            <TdNum>{fmt(p.scheduledprincipal)}</TdNum>
            <TdNum>{fmt(p.scheduledinterest)}</TdNum>
            <Td>{p.feetype || "—"}</Td>
            <TdNum>{fmt(p.scheduledfee)}</TdNum>
            <Td>{p.othertype || "—"}</Td>
            <TdNum>{fmt(p.scheduledother)}</TdNum>
            <TdNum style={{ fontWeight: 500 }}>{fmt(p.totalscheduledpayment)}</TdNum>
            <TdNum>{fmt(p.totalreceivedpmt)}</TdNum>
            <Td><div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }} onClick={e => e.stopPropagation()}>
              <Btn onClick={() => view(p)}><Icon type="view" /></Btn>
              {role.canEdit && <Btn onClick={() => setModal({ type: "pspmt-edit", rec: { ...p, id: p.id, loanid: p.loanid }, onDone: load })}><Icon type="edit" /></Btn>}
              {role.canDelete && <Btn variant="danger" onClick={() => setConfirm({ msg: "Delete this participant scheduled payment?",
                onConfirm: () => delRec("ParticipantScheduledPmts", p.id, "Deleted participant scheduled payment", load) })}><Icon type="delete" /></Btn>}
            </div></Td>
          </tr>))}</tbody>
      </table>
      {!rows.length && <div style={{ fontSize: 13, color: "var(--color-text-secondary)", padding: 12 }}>No participation scheduled payments recorded.</div>}
      <Pagination page={page} totalPages={totalPages} total={total} pageSize={100} onPage={setPage} />
    </Card>
  </>;
};
